/**
 * Thin axios wrappers around the Admin→Users management endpoints
 * (`/admin/users/*`), shipped as Phase 6 of the region-scoping feature.
 *
 * All requests go through the shared `client` (base URL + Authorization
 * header injection live there). Tenant scope is enforced server-side by the
 * JWT middleware; assigning `scopes` and creating/assigning the
 * `admin`/`org_manager` roles are ADMIN-only server-side — an ORG_MANAGER
 * caller will get a 403, which the UI surfaces inline.
 *
 * Pattern mirrors `src/api/dataChanges.ts`: bare `client.<verb>` + typed
 * return values + async/await, no mock fixtures.
 */
import client from '@/api/client';
import type { ProposalEnvelope } from '@/api/easy';
import type { AdminUser, UserScopeInput } from '@/types';

// ---------------------------------------------------------------------------
// Request bodies — keep in sync with the backend Pydantic models.
// ---------------------------------------------------------------------------

export interface CreateUserPayload {
  email: string;
  /**
   * OMIT THIS to use the invite flow, which is the intended path: the account
   * is created unusable, the response carries a one-time `invite_token`, and
   * the invitee chooses the only credential that ever authenticates them.
   * Send a password only to restore access for someone out of band.
   */
  password?: string;
  full_name?: string;
  role: string;
  /** Omit entirely to create an unscoped (whole-tenant) user. */
  scopes?: UserScopeInput[];

  /**
   * Makes this account the login FOR that candidate: it writes the
   * `relation='candidate'` link and mirrors the candidate's reach onto the
   * user. Requires `role: 'candidate'` and no `scopes` — the server 400s
   * otherwise, because reach comes from the candidate, not from the caller.
   */
  candidate_id?: number;

  /**
   * Required only when the chosen candidate has NO turf. Such a login is
   * tenant-global, which is correct for a county-wide office and identical to
   * a data-entry mistake, so the server refuses with a 409 until a human says
   * which one this is.
   */
  county_wide_confirmed?: boolean;

  /** 1..90. Server default is 14 (services/invites.DEFAULT_TTL_DAYS). */
  invite_ttl_days?: number;
}

export interface UpdateUserPayload {
  full_name?: string;
  role?: string;
  is_active?: boolean;
  /** Omit to leave the password unchanged. */
  password?: string;
  /** When present, replaces ALL of the user's scopes (replace-all semantics). */
  scopes?: UserScopeInput[];
}

// ---------------------------------------------------------------------------
// Endpoints.
// ---------------------------------------------------------------------------

/** List all users in the caller's tenant. */
export async function listAdminUsers(): Promise<AdminUser[]> {
  const res = await client.get<AdminUser[]>('/admin/users');
  return res.data;
}

/** Fetch a single user by id. */
export async function getAdminUser(id: number): Promise<AdminUser> {
  const res = await client.get<AdminUser>(`/admin/users/${id}`);
  return res.data;
}

/** Create a new user in the caller's tenant. */
export async function createAdminUser(payload: CreateUserPayload): Promise<AdminUser> {
  const res = await client.post<AdminUser>('/admin/users', payload);
  return res.data;
}

/**
 * Patch an existing user. Any field left undefined is untouched; when
 * `scopes` is present it replaces the user's entire scope set.
 */
export async function updateAdminUser(
  id: number,
  payload: UpdateUserPayload,
): Promise<AdminUser> {
  const res = await client.patch<AdminUser>(`/admin/users/${id}`, payload);
  return res.data;
}

/** Deactivate a user (soft — the account is retained, just disabled). */
export async function deactivateAdminUser(id: number): Promise<AdminUser> {
  const res = await client.post<AdminUser>(`/admin/users/${id}/deactivate`);
  return res.data;
}

/** Reactivate a previously-deactivated user via PATCH is_active=true. */
export async function reactivateAdminUser(id: number): Promise<AdminUser> {
  return updateAdminUser(id, { is_active: true });
}

/**
 * Mint a fresh invite for an existing user, revoking any outstanding one.
 *
 * Replacement, not addition — server-side, a link that leaked into a group
 * chat dies the moment this is called. That is also why this is the right
 * answer for a colleague who lost access, rather than an admin typing a new
 * password: the credential stays in exactly one person's hands.
 *
 * The returned `invite_token` is the ONLY time the raw token exists; it is
 * persisted only as a SHA-256 hash. Show it immediately.
 *
 * Requires a provisioner (ADMIN, or a user with `can_provision`) — a caller
 * without it gets a 403. The server 400s on a deactivated account; reactivate
 * first.
 */
export async function reissueInvite(
  id: number,
  ttlDays?: number,
): Promise<AdminUser> {
  const res = await client.post<AdminUser>(
    `/admin/users/${id}/invite`,
    null,
    ttlDays ? { params: { ttl_days: ttlDays } } : undefined,
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Easy button #3 — "Chase the unredeemed invites"
// ---------------------------------------------------------------------------
//
// See memory-bank/plans/easy-buttons.md §3. `reissueInvite` above, N times,
// with the same fence applied per target: this composition adds no permission
// of its own. The preview is gated identically to the commit, because it names
// accounts.

/** One unclaimed account, as the preview reports it. */
export interface PendingInviteItem {
  user_id: number;
  email: string;
  full_name: string | null;
  role: string;
  created_at: string | null;
  /** Whole days since the account was created. Null if the row has no date. */
  days_waiting: number | null;
  /**
   * `expired` — their link is dead, chase them. `none` — no link was ever
   * issued. `live` — their link still works, and re-issuing would KILL it;
   * only ever present when `include_valid` was set.
   */
  link_state: 'live' | 'expired' | 'none';
  selected: boolean;
}

export interface PendingInvitesResponse {
  proposal: ProposalEnvelope<PendingInviteItem>;
  /** Whom a press with these settings would re-invite, in the shown order. */
  user_ids: number[];
  include_valid: boolean;
}

export interface MintedInvite {
  user_id: number;
  email: string;
  full_name: string | null;
  role: string;
  /**
   * The raw token. This response is the only place it ever exists — it is
   * stored hashed and is not recoverable. Show it or lose it.
   */
  token: string;
  expires_at: string;
}

export interface BulkInviteResponse extends PendingInvitesResponse {
  reissued: number;
  invites: MintedInvite[];
}

/**
 * Who was invited and never showed up. Writes nothing, mints nothing.
 *
 * No arguments needed: the default population is every account in the caller's
 * reach that has never signed in and whose link is expired or missing.
 */
export async function fetchPendingInvites(
  opts: { include_valid?: boolean } = {},
  signal?: AbortSignal,
): Promise<PendingInvitesResponse> {
  const res = await client.get<PendingInvitesResponse>(
    '/admin/users/pending-invites',
    { params: { include_valid: opts.include_valid ?? false }, signal },
  );
  return res.data;
}

/**
 * Re-issue an invite for each planned account and return every raw link.
 *
 * ⚠️ Each mint REVOKES that account's outstanding link — replacement, never
 * addition. That is why `include_valid` exists and defaults to false: without
 * it the server leaves anyone whose link still works alone, so a link already
 * hand-delivered is not killed by a button pressed to help somebody else.
 *
 * `user_ids` can only NARROW: eligibility is re-derived server-side, so naming
 * an ineligible account does not invite it in.
 */
export async function bulkReissueInvites(params: {
  user_ids?: number[] | null;
  include_valid?: boolean;
  ttl_days?: number;
}): Promise<BulkInviteResponse> {
  const res = await client.post<BulkInviteResponse>('/admin/users/invite/bulk', {
    user_ids: params.user_ids ?? null,
    include_valid: params.include_valid ?? false,
    ...(params.ttl_days ? { ttl_days: params.ttl_days } : {}),
  });
  return res.data;
}
